import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Plus, Search, Filter, X, AlertTriangle, Download, Upload, List, LayoutGrid } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/formatters';
import DealsTable from '../components/deals/DealsTable';
import DealsKanban from '../components/deals/DealsKanban';
import DealsKpiTiles from '../components/deals/DealsKpiTiles';
import DealForm from '../components/deals/DealForm';
import DealWizard from '../components/deals/DealWizard';
import TenantBanner from '../components/TenantBanner';
import DealsCSVExportModal from '../components/deals/DealsCSVExportModal';
import DealsCSVImportModal from '../components/deals/DealsCSVImportModal';
import useDealUpdates from '../hooks/useDealUpdates';

// Import custom hooks
import { 
  useDeals, 
  useContacts, 
  useDealsFilter, 
  useDealsSelection,
  DEAL_STATUSES,
  getStatusBadgeColor,
  useRefinanceMode,
  useDealFormCalculations,
} from '../hooks/useDeals';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialFormData = {
  deal_id: '',
  deal_shortcode: '',
  broker_id: '',
  company_id: '',
  funder_id: '',
  funder_percentage: '100',
  deal_name: '',
  funding_date: new Date().toISOString().split('T')[0],
  funding_amount: '',
  fees: '0',
  refi_balance_payoff: '0',
  deposit_amount: '',
  rate: '',
  term_days: '',
  payback: '',
  payment_amount: '',
  estimated_end_date: '',
  weekly: false,
  status: 'Funded',
  broker_commission: '0',
  commission_rate: '',
  commission_type: '',
  refi: false,
  legal_business_name: '',
  ein_tax_id: '',
  business_type: '',
  years_in_business: '',
  monthly_revenue: '',
  owner_name: '',
  owner_ssn: '',
  owner_dob: '',
  owner_address: '',
  owner_phone: '',
  owner_email: '',
  business_address: '',
  notes: ''
};

const Deals = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAuthHeaders, user } = useAuth();
  
  // Get orphans filter from URL
  const showOrphans = searchParams.get('orphans') === 'true';
  
  // Use custom hooks - pass orphans param to API
  const queryParams = useMemo(() => showOrphans ? { orphans: 'true' } : {}, [showOrphans]);
  const { deals, loading, refetch: fetchDeals } = useDeals(getAuthHeaders, queryParams);
  const { brokers, merchants, funders } = useContacts(getAuthHeaders);
  const { 
    filteredDeals, 
    searchQuery, 
    setSearchQuery, 
    statusFilter,
    setStatusFilter,
    toggleStatusFilter, 
    clearStatusFilter,
    activeFilterCount 
  } = useDealsFilter(deals);
  const { 
    selectedDeals, 
    selectDeal, 
    selectAll, 
    clearSelection 
  } = useDealsSelection();

  // Local state for dialog and form
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState(initialFormData);
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [csvImportModalOpen, setCsvImportModalOpen] = useState(false);

  // List <-> Kanban view (persisted per device)
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('pfs_deals_view') === 'kanban' ? 'kanban' : 'list'; } catch { return 'list'; }
  });
  const changeViewMode = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem('pfs_deals_view', mode); } catch { /* ignore */ }
  };

  const isAdmin = user?.roles?.includes('admin');
  const isFunder = user?.roles?.includes('funder');

  // BL-020: subscribe to cross-tab deal updates and refetch the list whenever
  // any deal is mutated in another tab. Cheap — the list endpoint is already
  // optimized for portfolio render and this only fires on real events.
  useDealUpdates(useCallback(() => {
    fetchDeals();
  }, [fetchDeals]));

  // BL-020: refetch the list once whenever the operator returns focus to the
  // tab. Catches backend-triggered changes (cron, CSV import elsewhere,
  // multi-user edits) that BroadcastChannel can't see. Single fetch on focus
  // — no polling intervals.
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') {
        fetchDeals();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [fetchDeals]);
  
  // Use refinance mode hook (extracted from inline state)
  const {
    refinanceMode,
    refinanceFromDeal,
    refiIncludeFees,
    refinanceSyndications,
    initFromUrlParam: initRefinanceFromUrl,
    resetRefinanceMode,
    handleRefinanceSubmit,
    updateRefiBalance,
  } = useRefinanceMode(deals, getAuthHeaders, initialFormData, setFormData, setDialogOpen, navigate);

  // Use form calculations hook (extracted)
  const { handleFieldChange, preparePayload } = useDealFormCalculations(merchants, setFormData);

  // Toggle orphans filter
  const toggleOrphansFilter = () => {
    if (showOrphans) {
      searchParams.delete('orphans');
    } else {
      searchParams.set('orphans', 'true');
    }
    setSearchParams(searchParams);
  };

  // Handle status filter from URL params
  useEffect(() => {
    const statusParam = searchParams.get('status');
    if (statusParam && DEAL_STATUSES.includes(statusParam)) {
      setStatusFilter([statusParam]);
    }
  }, [searchParams, setStatusFilter]);

  // Handle URL params (refinance mode, createNew)
  useEffect(() => {
    // Handle refinance mode from URL - uses extracted hook
    const refinanceFromId = searchParams.get('refinance_from');
    if (refinanceFromId) {
      initRefinanceFromUrl(refinanceFromId);
    }
    
    // Handle createNew from URL (triggered from Contact Profile or Contacts page)
    const createNew = searchParams.get('createNew');
    if (createNew === 'true' && merchants.length > 0) {
      const preFillData = sessionStorage.getItem('preFillDeal');
      if (preFillData) {
        try {
          const contactData = JSON.parse(preFillData);
          const merchant = merchants.find(m => m.id === contactData.company_id);
          setFormData(prev => ({
            ...initialFormData,
            company_id: contactData.company_id || '',
            deal_name: contactData.company_name || '',
            deal_shortcode: contactData.shortcode || merchant?.shortcode || '',
            legal_business_name: contactData.company_name || '',
            // Use data from sessionStorage first, then fallback to merchant lookup
            business_address: contactData.business_address || merchant?.business_address || '',
            owner_name: contactData.owner_name || merchant?.owner_name || '',
            owner_phone: contactData.owner_phone || contactData.phone || merchant?.phone || '',
            owner_email: contactData.owner_email || contactData.email || merchant?.email || '',
            ein_tax_id: contactData.ein || merchant?.ein || '',
            monthly_revenue: contactData.monthly_revenue || merchant?.monthly_revenue || '',
            // Contract conversion (08/21): terms carried over from a completed
            // Signport contract. Empty for a plain "create deal from contact",
            // so this leaves the existing flow exactly as it was.
            funding_amount: contactData.funding_amount || initialFormData.funding_amount,
            payback: contactData.payback || initialFormData.payback,
            rate: contactData.rate || initialFormData.rate,
            term_days: contactData.term_days || initialFormData.term_days,
            payment_amount: contactData.payment_amount || initialFormData.payment_amount,
            fees: contactData.fees || initialFormData.fees,
            weekly: String(contactData.schedule || '').toLowerCase().startsWith('week'),
          }));
          sessionStorage.removeItem('preFillDeal');
        } catch (e) {
          console.error('Error parsing pre-fill data:', e);
        }
      }
      setDialogOpen(true);
      navigate('/deals', { replace: true });
    }
  }, [searchParams, merchants, navigate, initRefinanceFromUrl]);
      
  // Contract conversion (08/21). A completed Signport contract sends the
  // operator here with the form pre-filled; the contract is attached only once
  // the funded deal actually exists, so signing never funds anything by itself.
  // The merchant is re-checked so a stale hand-off can't latch onto an
  // unrelated deal created later.
  const linkPendingContract = useCallback(async (createdDeal) => {
    const raw = sessionStorage.getItem('pendingContractLink');
    if (!raw || !createdDeal?.id) return;
    sessionStorage.removeItem('pendingContractLink');
    let pending = null;
    try {
      pending = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!pending?.documentId) return;
    if (pending.contactId && createdDeal.company_id !== pending.contactId) return;
    try {
      await axios.post(
        `${API}/contracts/${pending.documentId}/convert`,
        { deal_ref: createdDeal.id },
        { headers: getAuthHeaders() }
      );
      toast.success('Signed contract and audit certificate attached to the new deal');
    } catch (err) {
      toast.error(err.response?.data?.detail
        || 'Deal created, but the contract did not attach — link it from Contracts');
    }
  }, [getAuthHeaders]);

  const handleOpenDialog = useCallback((deal = null) => {
    if (deal) {
      setEditingDeal(deal);
      setFormData({
        ...initialFormData,
        ...deal,
        deal_id: deal.deal_id?.toString() || '',
        funding_date: deal.funding_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        estimated_end_date: deal.estimated_end_date?.split('T')[0] || '',
        owner_dob: deal.owner_dob?.split('T')[0] || '',
        funding_amount: deal.funding_amount?.toString() || '',
        payback: deal.payback?.toString() || '',
        payment_amount: deal.payment_amount?.toString() || '',
        rate: deal.rate?.toString() || '',
        term_days: deal.term_days?.toString() || '',
        fees: deal.fees?.toString() || '0',
        refi_balance_payoff: deal.refi_balance_payoff?.toString() || '0',
        deposit_amount: deal.deposit_amount?.toString() || '',
        funder_percentage: deal.funder_percentage?.toString() || '100',
        broker_commission: deal.broker_commission?.toString() || '0',
        commission_rate: deal.commission_rate?.toString() || '',
        years_in_business: deal.years_in_business?.toString() || '',
        monthly_revenue: deal.monthly_revenue?.toString() || ''
      });
    } else {
      setEditingDeal(null);
      setFormData(initialFormData);
    }
    setError('');
    setDialogOpen(true);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!editingDeal && !formData.company_id) {
      setError('A merchant (contact) must be selected to create a deal.');
      return;
    }

    // Use preparePayload from hook
    const payload = preparePayload(formData, !!editingDeal);

    try {
      if (editingDeal) {
        // Use PATCH for partial updates (only sends non-null fields)
        await axios.patch(`${API}/deals/${editingDeal.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Deal updated successfully');
      } else if (refinanceMode && refinanceFromDeal) {
        // Use handleRefinanceSubmit from hook (triggered from deal detail "Refinance Into New Deal" button)
        const result = await handleRefinanceSubmit(payload);
        
        if (result.syndicationsCopied > 0) {
          toast.success(`Refinance complete! Copied ${result.syndicationsCopied} syndicator(s)`);
        } else {
          toast.success(`Refinance complete! ${result.message}`);
        }
        
        resetRefinanceMode();
        navigate(`/deals/${result.newDealId}`);
      } else if (formData.refi && formData.refi_from_deal) {
        // Refinance via "Create New Deal" form with refi checkbox
        // Route to the refinance endpoint which handles payoff transaction + old deal closure
        payload.refi_balance_payoff = parseFloat(formData.refi_balance_payoff) || 0;
        const response = await axios.post(
          `${API}/deals/${formData.refi_from_deal}/refinance`,
          payload,
          { headers: getAuthHeaders() }
        );
        const newDealId = response.data.new_deal_id;
        toast.success('Refinance deal created! Payoff applied to old deal.');
        setDialogOpen(false);
        fetchDeals();
        navigate(`/deals/${newDealId}`);
        return;
      } else {
        const created = await axios.post(`${API}/deals`, payload, { headers: getAuthHeaders() });
        toast.success('Deal created successfully');
        await linkPendingContract(created.data);
      }
      setDialogOpen(false);
      fetchDeals();
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      let errorMessage = 'Operation failed';
      
      if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => `${e.loc[e.loc.length - 1]}: ${e.msg}`).join(', ');
      } else if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      }
      
      setError(errorMessage);
      // 08/05: the error banner renders above the fold in a scrolled dialog —
      // failures looked like "nothing happened". Toast so it's always visible.
      toast.error(errorMessage);
    }
  };

  const handleDelete = useCallback(async (dealId) => {
    if (!window.confirm('Are you sure? This will delete all related syndications and payments.'))
      return;

    try {
      await axios.delete(`${API}/deals/${dealId}`, { headers: getAuthHeaders() });
      toast.success('Deal deleted successfully');
      fetchDeals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete deal');
    }
  }, [getAuthHeaders, fetchDeals]);

  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      selectAll(filteredDeals);
    } else {
      clearSelection();
    }
  }, [filteredDeals, selectAll, clearSelection]);

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Delete ${selectedDeals.length} selected deal(s)?`)) return;
    
    try {
      for (const id of selectedDeals) {
        await axios.delete(`${API}/deals/${id}`, { headers: getAuthHeaders() });
      }
      toast.success(`Deleted ${selectedDeals.length} deals`);
      clearSelection();
      fetchDeals();
    } catch (err) {
      toast.error('Failed to delete some deals');
    }
  };

  const handleRowClick = useCallback((deal) => navigate(`/deals/${deal.id}`), [navigate]);

  // Kanban drag -> status change. Status changes have side effects, so always
  // confirm with the operator before PATCHing. Never bypasses backend rules.
  const handleKanbanStatusChange = useCallback(async (deal, newStatus) => {
    const ok = window.confirm(
      `Move deal #${deal.deal_id} (${deal.deal_name || 'Unnamed'}) from "${deal.status}" to "${newStatus}"?`
    );
    if (!ok) return;
    try {
      await axios.patch(`${API}/deals/${deal.id}`, { status: newStatus }, { headers: getAuthHeaders() });
      toast.success(`Deal #${deal.deal_id} moved to ${newStatus}`);
      fetchDeals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update deal status');
    }
  }, [getAuthHeaders, fetchDeals]);

  return (
    <Layout>
      <div className="px-4 sm:px-0 flex flex-col" style={{ height: 'calc(100vh - 88px)' }}>
        {/* Tenant Banner - shows when on validation or when live is empty */}
        <TenantBanner dealCount={deals.length} loading={loading} />
        
        {/* Orphan Deals Banner */}
        {showOrphans && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-orange-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-orange-800">Showing Orphan Deals</p>
              <p className="text-sm text-orange-600">
                These deals don't have a merchant contact linked. Click Edit to assign a merchant.
              </p>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={toggleOrphansFilter}
              className="text-orange-600 border-orange-300 hover:bg-orange-100"
            >
              <X className="h-4 w-4 mr-1" />
              Clear Filter
            </Button>
          </div>
        )}
        
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Deals</h1>
          <div className="flex items-center gap-3">
            {/* List / Kanban view toggle */}
            <div className="flex items-center rounded-md border border-gray-300 overflow-hidden" data-testid="deals-view-toggle">
              <Button
                type="button"
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none"
                onClick={() => changeViewMode('list')}
              >
                <List className="h-4 w-4 mr-1" /> List
              </Button>
              <Button
                type="button"
                variant={viewMode === 'kanban' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none"
                onClick={() => changeViewMode('kanban')}
              >
                <LayoutGrid className="h-4 w-4 mr-1" /> Kanban
              </Button>
            </div>
            {/* Orphan Deals Filter (Admin only) */}
            {isAdmin && (
              <Button 
                variant={showOrphans ? "default" : "outline"} 
                onClick={toggleOrphansFilter}
                className={showOrphans ? "bg-orange-600 hover:bg-orange-700" : "text-orange-600 border-orange-300 hover:bg-orange-50"}
                data-testid="orphan-deals-filter"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Orphan Deals
                {showOrphans && <X className="h-4 w-4 ml-2" />}
              </Button>
            )}

            {/* Download CSV */}
            <Button
              variant="outline"
              onClick={() => setCsvModalOpen(true)}
              className="text-green-700 border-green-300 hover:bg-green-50"
              data-testid="download-csv-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Download CSV
            </Button>

            {/* Import CSV */}
            <Button
              variant="outline"
              onClick={() => setCsvImportModalOpen(true)}
              className="text-purple-700 border-purple-300 hover:bg-purple-50"
              data-testid="import-csv-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Import CSV
            </Button>
            
            {/* Status Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="relative">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter Status
                  {activeFilterCount > 0 && (
                    <Badge className="ml-2 bg-blue-600 text-white h-5 w-5 p-0 flex items-center justify-center text-xs">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-3" align="end">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium">Filter by Status</span>
                  {activeFilterCount > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={clearStatusFilter}
                      className="h-6 px-2 text-xs"
                    >
                      Clear all
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {DEAL_STATUSES.map(status => (
                    <div key={status} className="flex items-center space-x-2">
                      <Checkbox
                        id={`status-${status}`}
                        checked={statusFilter.includes(status)}
                        onCheckedChange={() => toggleStatusFilter(status)}
                      />
                      <label 
                        htmlFor={`status-${status}`} 
                        className="text-sm cursor-pointer flex-1 flex items-center justify-between"
                      >
                        <span>{status}</span>
                        <Badge className={`${getStatusBadgeColor(status)} text-xs`}>
                          {deals.filter(d => d.status === status).length}
                        </Badge>
                      </label>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by name, ID, or shortcode..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-80"
              />
            </div>
            {isAdmin && (
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open && refinanceMode) {
                  resetRefinanceMode();
                  navigate('/deals', { replace: true });
                }
              }}>
                <DialogTrigger asChild>
                  <Button onClick={() => handleOpenDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Deal
                  </Button>
                </DialogTrigger>
                <DialogContent className={`${editingDeal || refinanceMode ? 'max-w-6xl' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
                  <DialogHeader>
                    <DialogTitle className="text-2xl">
                      {refinanceMode ? `Refinance Deal #${refinanceFromDeal?.deal_id}` : (editingDeal ? 'Edit Deal' : 'Create New Deal')}
                    </DialogTitle>
                    {refinanceMode && refinanceFromDeal && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-2">
                        <p className="text-sm font-medium text-blue-800">Refinancing from Deal #{refinanceFromDeal.deal_id}</p>
                        
                        {/* Balance Selection - uses updateRefiBalance from hook */}
                        <div className="mt-3 p-3 bg-white rounded border border-blue-200">
                          <p className="text-sm font-medium text-blue-800 mb-2">Select Payoff Amount:</p>
                          <div className="flex gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="refiBalanceType"
                                checked={refiIncludeFees}
                                onChange={() => updateRefiBalance(true)}
                                className="text-blue-600"
                              />
                              <span className="text-sm text-blue-700">
                                With Fees: <span className="font-semibold">{formatCurrency(refinanceFromDeal.with_fees || refinanceFromDeal.balance || 0)}</span>
                              </span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="refiBalanceType"
                                checked={!refiIncludeFees}
                                onChange={() => updateRefiBalance(false)}
                                className="text-blue-600"
                              />
                              <span className="text-sm text-blue-700">
                                Without Fees: <span className="font-semibold">{formatCurrency(refinanceFromDeal.balance || 0)}</span>
                              </span>
                            </label>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-4 gap-4 mt-3 text-sm text-blue-700">
                          <div>
                            <span className="text-blue-600">Selected Payoff:</span>
                            <span className="font-semibold ml-1">{formatCurrency(parseFloat(formData.refi_balance_payoff || 0))}</span>
                          </div>
                          <div>
                            <span className="text-blue-600">Fees:</span>
                            <span className="font-semibold ml-1">{formatCurrency(parseFloat(formData.fees || 0))}</span>
                          </div>
                          <div>
                            <span className="text-blue-600">Total Fees (Original):</span>
                            <span className="font-semibold ml-1">{formatCurrency(refinanceFromDeal.total_fees || 0)}</span>
                          </div>
                          <div className="bg-green-100 rounded px-2 py-1">
                            <span className="text-green-700 font-medium">Net to Merchant:</span>
                            <span className="font-bold ml-1 text-green-800">
                              {formatCurrency((parseFloat(formData.funding_amount) || 0) - (parseFloat(formData.fees) || 0) - (parseFloat(formData.refi_balance_payoff) || 0))}
                            </span>
                          </div>
                        </div>
                        
                        {/* Syndications to copy */}
                        {refinanceSyndications.length > 0 && (
                          <div className="mt-3 p-3 bg-white rounded border border-blue-200">
                            <p className="text-sm font-medium text-blue-800 mb-2">
                              Syndicators to copy ({refinanceSyndications.length}):
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {refinanceSyndications.map(syn => (
                                <span key={syn.id} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                                  {syn.syndicator_name || 'Unknown'}: {syn.ownership_percentage}%
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {!refinanceMode && (
                      <p className="text-sm text-gray-500">
                        Fill in the required information to create a deal.
                      </p>
                    )}
                  </DialogHeader>
                  {/* Use wizard for new deal creation; DealForm for edit/refinance */}
                  {!editingDeal && !refinanceMode ? (
                    <DealWizard
                      onSubmit={async (payload) => {
                        setError('');
                        try {
                          const refi = payload._refi;
                          if (refi) delete payload._refi;
                          const res = await axios.post(`${API}/deals`, payload, { headers: getAuthHeaders() });
                          if (refi && res.data?.id) {
                            await axios.post(`${API}/refinance/apply`, {
                              new_deal_id: res.data.id,
                              items: [{ old_deal_id: refi.from_deal_uuid, payoff_amount: refi.payoff_amount }],
                            }, { headers: getAuthHeaders() });
                            toast.success('Deal created and refinance applied');
                          } else {
                            toast.success('Deal created successfully');
                          }
                          setDialogOpen(false);
                          fetchDeals();
                        } catch (err) {
                          const detail = err.response?.data?.detail;
                          const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? (detail[0]?.msg || JSON.stringify(detail)) : 'Failed to create deal';
                          setError(msg);
                          throw new Error(msg);
                        }
                      }}
                      onCancel={() => setDialogOpen(false)}
                      brokers={brokers}
                      funders={funders}
                      error={error}
                    />
                  ) : (
                    <DealForm
                      formData={formData}
                      setFormData={setFormData}
                      onSubmit={handleSubmit}
                      onCancel={() => setDialogOpen(false)}
                      error={error}
                      editingDeal={editingDeal}
                      brokers={brokers}
                      merchants={merchants}
                      funders={funders}
                      handleFieldChange={handleFieldChange}
                      allDeals={deals}
                      getAuthHeaders={getAuthHeaders}
                    />
                  )}
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* KPI stat tiles */}
        {!loading && deals.length > 0 && <DealsKpiTiles deals={deals} />}

        {/* Selected deals actions */}
        {selectedDeals.length > 0 && (isAdmin || isFunder) && (
          <Card className="mb-4 bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-blue-900 font-medium">
                  {selectedDeals.length} deal(s) selected
                </span>
                <Button variant="destructive" size="sm" onClick={handleDeleteSelected}>
                  Delete Selected
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Filters Display */}
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="text-sm text-gray-600">Filtering by:</span>
            {statusFilter.map(status => (
              <Badge 
                key={status} 
                className={`${getStatusBadgeColor(status)} cursor-pointer hover:opacity-80`}
                onClick={() => toggleStatusFilter(status)}
              >
                {status}
                <X className="h-3 w-3 ml-1" />
              </Badge>
            ))}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearStatusFilter}
              className="h-6 px-2 text-xs text-gray-500"
            >
              Clear all
            </Button>
            <span className="text-sm text-gray-500 ml-2">
              ({filteredDeals.length} of {deals.length} deals)
            </span>
          </div>
        )}

        {/* Deals Table */}
        <Card className="flex-1 min-h-0 flex flex-col">
          <CardContent className="p-0 flex-1 min-h-0 overflow-y-auto">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : deals.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No deals found. Create your first deal.
              </div>
            ) : viewMode === 'kanban' ? (
              <div className="p-3 h-full flex flex-col">
                <DealsKanban
                  deals={filteredDeals}
                  onStatusChange={handleKanbanStatusChange}
                  onCardClick={handleRowClick}
                  getStatusBadgeColor={getStatusBadgeColor}
                />
              </div>
            ) : (
              <DealsTable
                deals={filteredDeals}
                selectedDeals={selectedDeals}
                onSelectDeal={selectDeal}
                onSelectAll={handleSelectAll}
                onEdit={handleOpenDialog}
                onDelete={handleDelete}
                onRowClick={handleRowClick}
                isAdmin={isAdmin}
                getStatusBadgeColor={getStatusBadgeColor}
              />
            )}
          </CardContent>
        </Card>

        {/* CSV Export Modal */}
        <DealsCSVExportModal
          open={csvModalOpen}
          onClose={() => setCsvModalOpen(false)}
          deals={filteredDeals}
        />

        {/* CSV Import Modal */}
        <DealsCSVImportModal
          open={csvImportModalOpen}
          onClose={() => setCsvImportModalOpen(false)}
          onImportComplete={fetchDeals}
        />
      </div>
    </Layout>
  );
};

export default Deals;
